import { create } from 'zustand'

export type Trigger = {
  id: string
  trigger_id?: string  // 数据库中的真实 trigger ID，用于 API 调用
  title: string
  explanation: string
  impact: string
  rating: number | null
  isCustom?: boolean  // 标识是否为用户自定义的trigger
}

export type AnalysisState = {
  postContent: string | null
  imageUrl: string | null
  triggers: Trigger[]
  removedTriggers: Trigger[]
  followUpQuestion: string | null
  followUpAnswer: string | null
  currentStep: 'initial' | 'analysis' | 'first-follow-up' | 'second-follow-up' | 'third-follow-up' | 'complete'
  isLoading: boolean
  error: string | null
  language: 'zh' | 'en'
  analysisStartTime: number | null
  currentAnalysisTime: number | null
  averageAnalysisTime: number | null
  keyword: string | null
  highlightedTexts: { text: string; triggerId: string }[]
  userIdentity: {
    gender: string
    ageGroup: string
    occupation: string
    otherInfo: string
  } | null
  rawAnalysisResult: any
  isLoggedIn: boolean
  sessionKey: string | null
  currentImageId: string | null
}

type AnalysisStore = AnalysisState & {
  setPostContent: (content: string | null) => void
  setImageUrl: (url: string | null) => void
  setTriggers: (triggers: Trigger[]) => void
  setRemovedTriggers: (triggers: Trigger[]) => void
  setTriggerRating: (id: string, rating: number) => void
  setFollowUpQuestion: (question: string | null) => void
  setFollowUpAnswer: (answer: string | null) => void
  setCurrentStep: (step: AnalysisState['currentStep']) => void
  setIsLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setLanguage: (language: 'zh' | 'en') => void
  setAnalysisStartTime: (time: number | null) => void
  setCurrentAnalysisTime: (time: number | null) => void
  setAverageAnalysisTime: (time: number | null) => void
  setKeyword: (keyword: string | null) => void
  setUserIdentity: (identity: AnalysisState['userIdentity']) => void
  setRawAnalysisResult: (result: any) => void
  setCurrentImageId: (imageId: string | null) => void
  reset: () => void
  addTrigger: (trigger: Trigger) => void
  updateTrigger: (id: string, updates: Partial<Trigger>) => void
  deleteTrigger: (id: string) => void
  addHighlightedText: (text: string, triggerId: string) => void
  clearHighlightedTexts: () => void
  login: (sessionKey: string) => void
  logout: () => void
  initializeAuth: () => void
}

const initialState: AnalysisState = {
  postContent: null,
  imageUrl: null,
  triggers: [],
  removedTriggers: [],
  followUpQuestion: null,
  followUpAnswer: null,
  currentStep: 'initial',
  isLoading: false,
  error: null,
  language: 'zh',
  analysisStartTime: null,
  currentAnalysisTime: null,
  averageAnalysisTime: null,
  keyword: null,
  highlightedTexts: [],
  userIdentity: null,
  rawAnalysisResult: null,
  isLoggedIn: false,
  sessionKey: null,
  currentImageId: null
}

export const useAnalysisStore = create<AnalysisStore>((set, get) => ({
  ...initialState,
  setPostContent: (content) => set({ postContent: content }),
  setImageUrl: (url) => set({ imageUrl: url }),
  setTriggers: (triggers) => set({ triggers }),
  setRemovedTriggers: (triggers) => set({ removedTriggers: triggers }),
  setTriggerRating: (id: string, rating: number) => {
    set((state) => ({
      triggers: state.triggers.map((trigger) =>
        trigger.id === id ? { ...trigger, rating } : { ...trigger }
      ),
    }))
  },
  setFollowUpQuestion: (question) => set({ followUpQuestion: question }),
  setFollowUpAnswer: (answer) => set({ followUpAnswer: answer }),
  setCurrentStep: (step) => set({ currentStep: step }),
  setIsLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  setLanguage: (language) => set({ language }),
  setAnalysisStartTime: (time) => set({ analysisStartTime: time }),
  setCurrentAnalysisTime: (time) => set({ currentAnalysisTime: time }),
  setAverageAnalysisTime: (time) => set({ averageAnalysisTime: time }),
  setKeyword: (keyword) => set({ keyword }),
  setUserIdentity: (identity) => set({ userIdentity: identity }),
  setRawAnalysisResult: (result) => set({ rawAnalysisResult: result }),
  setCurrentImageId: (imageId) => set({ currentImageId: imageId }),
  reset: () => set({ ...initialState, isLoggedIn: get().isLoggedIn, sessionKey: get().sessionKey, currentImageId: null }),
  addTrigger: (trigger) =>
    set((state) => ({
      triggers: [...state.triggers, trigger],
    })),
  updateTrigger: (id, updates) =>
    set((state) => ({
      triggers: state.triggers.map((trigger) =>
        trigger.id === id ? { ...trigger, ...updates } : trigger
      ),
    })),
  deleteTrigger: (id) =>
    set((state) => ({
      triggers: state.triggers.filter((trigger) => trigger.id !== id),
    })),
  addHighlightedText: (text, triggerId) =>
    set((state) => ({
      highlightedTexts: [...state.highlightedTexts, { text, triggerId }],
    })),
  clearHighlightedTexts: () =>
    set(() => ({
      highlightedTexts: [],
    })),
  login: (sessionKey: string) => {
    localStorage.setItem('userId', sessionKey.trim());
    set({ isLoggedIn: true, sessionKey: sessionKey.trim() });
  },
  logout: () => {
    // 清除用户认证信息
    localStorage.removeItem('userId');
    
    // 清除用户个人信息 (包括表单和缓存)
    localStorage.removeItem('userIdentity');
    localStorage.removeItem('cachedUserInfo');
    
    // 清除所有分析数据和上传的图片
    localStorage.removeItem('allAnxietyData');
    
    // 清除分析时间相关数据
    localStorage.removeItem('analysisTimes');
    localStorage.removeItem('averageAnalysisTime');
    
    // 注意：保留语言设置 'language'，因为这是用户偏好设置
    
    // 重置应用状态
    set({ ...initialState });
  },
  initializeAuth: () => {
    const savedSessionKey = localStorage.getItem('userId');
    if (savedSessionKey) {
      set({ isLoggedIn: true, sessionKey: savedSessionKey });
    }
  }
})) 